import React from 'react';

import styles from './PrivacyPolicyText.module.css';

export const PrivacyPolicyText = () => (
  <div className={styles['body']}>
    <div>
      <strong>
        <span className={styles['title']} style={{ visibility: 'hidden' }}>
          <h1>PRIVACY POLICY</h1>
        </span>
      </strong>
    </div>
    <div>
      <strong>
        <span className={styles['subtitle']}>
          Last updated February 01, 2025
        </span>
      </strong>
    </div>
    <div style={{ height: '1em' }} />
    <div className={styles['body_text']}>
      This Privacy Notice for <strong>Aquifer, Inc</strong> ("
      <strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"),
      describes how and why we might access, collect, store, use, and/or share
      ("<strong>process</strong>") your personal information when you use our
      services ("<strong>Services</strong>"), including when you:
    </div>
    <ul>
      <li className={styles['body_text']}>
        Visit our website at{' '}
        <span style={{ color: 'rgb(0, 58, 250)' }}>
          https://app.aquifer.cloud
        </span>
        , or any website of ours that links to this Privacy Notice
      </li>
      <li className={styles['body_text']}>
        Engage with us in other related ways, including any sales, marketing, or
        events
      </li>
    </ul>
    <div className={styles['body_text']}>
      <strong>Questions or concerns?</strong> Reading this Privacy Notice will
      help you understand your privacy rights and choices. We are responsible
      for making decisions about how your personal information is processed. If
      you do not agree with our policies and practices, please do not use our
      Services. If you still have any questions or concerns, please contact us
      at{' '}
      <a
        href="mailto:notices@aquifer.cloud"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        notices@aquifer.cloud
      </a>
      .
    </div>
    <div style={{ height: '1em' }} />
    <div>
      <strong>
        <span className={styles['heading_1']}>
          <h2>SUMMARY OF KEY POINTS</h2>
        </span>
      </strong>
    </div>
    <div className={styles['body_text']}>
      <strong>
        <em>
          This summary provides key points from our Privacy Notice, but you can
          find out more details about any of these topics by clicking the link
          following each key point or by using our{' '}
          <a
            className={styles['link']}
            href="#toc"
            style={{ color: 'rgb(0, 58, 250)' }}
          >
            table of contents
          </a>{' '}
          below to find the section you are looking for.
        </em>
      </strong>
    </div>
    <div style={{ height: '1em' }} />
    <div
      style={{ lineHeight: 1.5, color: 'rgb(89, 89, 89)', fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>What personal information do we process?</strong> When you visit,
      use, or navigate our Services, we may process personal information
      depending on how you interact with us and the Services, the choices you
      make, and the products and features you use. Learn more about{' '}
      <a
        className={styles['link']}
        href="#personalinfo"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        personal information you disclose to us
      </a>
      .
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, color: 'rgb(89, 89, 89)', fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>Do we process any sensitive personal information?</strong> Some of
      the information may be considered "special" or "sensitive" in certain
      jurisdictions, for example your racial or ethnic origins, sexual
      orientation, and religious beliefs. We do not process sensitive personal
      information.
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, color: 'rgb(89, 89, 89)', fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>Do we collect any information from third parties?</strong> We do
      not collect any information from third parties.
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, color: 'rgb(89, 89, 89)', fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>How do we process your information?</strong> We process your
      information to provide, improve, and administer our Services, communicate
      with you, for security and fraud prevention, and to comply with law. We
      may also process your information for other purposes with your consent. We
      process your information only when we have a valid legal reason to do so.
      Learn more about{' '}
      <a
        className={styles['link']}
        href="#infouse"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        how we process your information
      </a>
      .
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>
        In what situations and with which parties do we share personal
        information?
      </strong>{' '}
      We may share information in specific situations and with specific third
      parties. Learn more about{' '}
      <a
        className={styles['link']}
        href="#whoshare"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        when and with whom we share your personal information
      </a>
      .
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>How do we keep your information safe?</strong> We have adequate
      organizational and technical processes and procedures in place to protect
      your personal information. However, no electronic transmission over the
      internet or information storage technology can be guaranteed to be 100%
      secure, so we cannot promise or guarantee that hackers, cybercriminals, or
      other unauthorized third parties will not be able to defeat our security
      and improperly collect, access, steal, or modify your information. Learn
      more about{' '}
      <a
        className={styles['link']}
        href="#infosafe"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        how we keep your information safe
      </a>
      .
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>What are your rights?</strong> Depending on where you are located
      geographically, the applicable privacy law may mean you have certain
      rights regarding your personal information. Learn more about{' '}
      <a
        className={styles['link']}
        href="#privacyrights"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        your privacy rights
      </a>
      .
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, fontSize: 15 }}
      className={styles['body_text']}
    >
      <strong>How do you exercise your rights?</strong> The easiest way to
      exercise your rights is by{' '}
      <a
        className={styles['link']}
        href="https://app.termly.io/notify/cd8bac8a-12fd-469c-99ac-de2ab0a6d7a4"
        rel="noopener noreferrer"
        target="_blank"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        data subject access request
      </a>
      , or by contacting us. We will consider and act upon any request in
      accordance with applicable data protection laws.
    </div>
    <div style={{ height: '1em' }}></div>
    <div
      style={{ lineHeight: 1.5, fontSize: 15 }}
      className={styles['body_text']}
    >
      Want to learn more about what we do with any information we collect?{' '}
      <a
        className={styles['link']}
        href="#toc"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        Review the Privacy Notice in full
      </a>
      .
    </div>
    <div style={{ height: '2em' }} />
    <div id="toc">
      <strong>
        <span className={styles['heading_1']}>
          <h2>TABLE OF CONTENTS</h2>
        </span>
      </strong>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#infocollect"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        1. WHAT INFORMATION DO WE COLLECT?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#infouse"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        2. HOW DO WE PROCESS YOUR INFORMATION?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#legalbases"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#whoshare"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#cookies"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#inforetain"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        6. HOW LONG DO WE KEEP YOUR INFORMATION?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#infosafe"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        7. HOW DO WE KEEP YOUR INFORMATION SAFE?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#infominors"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        8. DO WE COLLECT INFORMATION FROM MINORS?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#privacyrights"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        9. WHAT ARE YOUR PRIVACY RIGHTS?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#DNT"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        10. CONTROLS FOR DO-NOT-TRACK FEATURES
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#uslaws"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        11. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#policyupdates"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        12. DO WE MAKE UPDATES TO THIS NOTICE?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#contact"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
      </a>
    </div>
    <div>
      <a
        className={styles['link']}
        href="#request"
        style={{ color: 'rgb(0, 58, 250)' }}
      >
        14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
      </a>
    </div>
    <div id="infocollect">
      <strong>
        <span className={styles['heading_1']}>
          <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
        </span>
      </strong>
      <span className={styles['heading_2']} id="personalinfo">
        <strong>
          <h3>Personal information you disclose to us</h3>
        </strong>
      </span>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short:</em>
        </strong>{' '}
        We collect personal information that you provide to us.
      </span>
      <div className={styles['body_text']}>
        We collect personal information that you voluntarily provide to us when
        you register on the Services, express an interest in obtaining
        information about us or our products and Services, when you participate
        in activities on the Services, or otherwise when you contact us.
      </div>
      <div className={styles['body_text']}>
        <strong>Personal Information Provided by You.</strong> The personal
        information that we collect depends on the context of your interactions
        with us and the Services, the choices you make, and the products and
        features you use. The personal information we collect may include the
        following:
      </div>
      <ul>
        <li className={styles['body_text']}>names</li>
        <li className={styles['body_text']}>email addresses</li>
        <li className={styles['body_text']}>usernames</li>
        <li className={styles['body_text']}>passwords</li>
        <li className={styles['body_text']}>contact preferences</li>
        <li className={styles['body_text']}>contact or authentication data</li>
      </ul>
      <div className={styles['body_text']}>
        <strong>Sensitive Information.</strong> We do not process sensitive
        information.
      </div>
      <div className={styles['body_text']}>
        All personal information that you provide to us must be true, complete,
        and accurate, and you must notify us of any changes to such personal
        information.
      </div>
      <span className={styles['heading_2']} style={{ color: 'rgb(0,0,0)' }}>
        <strong>
          <h3>Information automatically collected</h3>
        </strong>
      </span>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short:</em>
        </strong>{' '}
        Some information — such as your Internet Protocol (IP) address and/or
        browser and device characteristics — is collected automatically when you
        visit our Services.
      </span>
      <div className={styles['body_text']}>
        We automatically collect certain information when you visit, use, or
        navigate the Services. This information does not reveal your specific
        identity (like your name or contact information) but may include device
        and usage information, such as your IP address, browser and device
        characteristics, operating system, language preferences, referring URLs,
        device name, country, location, information about how and when you use
        our Services, and other technical information. This information is
        primarily needed to maintain the security and operation of our Services,
        and for our internal analytics and reporting purposes.
      </div>
      <div className={styles['body_text']}>
        Like many businesses, we also collect information through cookies and
        similar technologies.
      </div>
      <div className={styles['body_text']}>
        The information we collect includes:
      </div>
      <ul>
        <li className={styles['body_text']}>
          <em>Log and Usage Data.</em> Log and usage data is service-related,
          diagnostic, usage, and performance information our servers
          automatically collect when you access or use our Services and which we
          record in log files. Depending on how you interact with us, this log
          data may include your IP address, device information, browser type,
          and settings and information about your activity in the Services (such
          as the date/time stamps associated with your usage, pages and files
          viewed, searches, and other actions you take such as which features
          you use), device event information (such as system activity, error
          reports (sometimes called "crash dumps"), and hardware settings).
        </li>
        <li className={styles['body_text']}>
          <em>Device Data.</em> We collect device data such as information about
          your computer, phone, tablet, or other device you use to access the
          Services. Depending on the device used, this device data may include
          information such as your IP address (or proxy server), device and
          application identification numbers, location, browser type, hardware
          model, Internet service provider and/or mobile carrier, operating
          system, and system configuration information.
        </li>
      </ul>
      <div className={styles['body_text']}>
        <strong>
          <span className={styles['heading_2']}>
            <h3>Google API</h3>
          </span>
        </strong>{' '}
        Our use of information received from Google APIs will adhere to{' '}
        <a
          className={styles['link']}
          href="https://developers.google.com/terms/api-services-user-data-policy"
          rel="noopener noreferrer"
          target="_blank"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          Google API Services User Data Policy
        </a>
        , including the{' '}
        <a
          className={styles['link']}
          href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
          rel="noopener noreferrer"
          target="_blank"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          Limited Use requirements
        </a>
        .
      </div>
    </div>
    <div id="infouse">
      <strong>
        <span className={styles['heading_1']}>
          <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short: </em>
        </strong>
        We process your information to provide, improve, and administer our
        Services, communicate with you, for security and fraud prevention, and
        to comply with law. We may also process your information for other
        purposes with your consent.
      </span>
      <div className={styles['body_text']}>
        <strong>
          We process your personal information for a variety of reasons,
          depending on how you interact with our Services, including:
        </strong>
      </div>
      <ul>
        <li className={styles['body_text']}>
          <strong>
            To facilitate account creation and authentication and otherwise
            manage user accounts.
          </strong>{' '}
          We may process your information so you can create and log in to your
          account, as well as keep your account in working order.
        </li>
        <li className={styles['body_text']}>
          <strong>
            To deliver and facilitate delivery of services to the user.
          </strong>{' '}
          We may process your information to provide you with the requested
          service.
        </li>
        <li className={styles['body_text']}>
          <strong>To respond to user inquiries/offer support to users.</strong>{' '}
          We may process your information to respond to your inquiries and solve
          any potential issues you might have with the requested service.
        </li>
        <li className={styles['body_text']}>
          <strong>To send administrative information to you.</strong> We may
          process your information to send you details about our products and
          services, changes to our terms and policies, and other similar
          information.
        </li>
        <li className={styles['body_text']}>
          <strong>To request feedback.</strong> We may process your information
          when necessary to request feedback and to contact you about your use
          of our Services.
        </li>
        <li className={styles['body_text']}>
          <strong>To protect our Services.</strong> We may process your
          information as part of our efforts to keep our Services safe and
          secure, including fraud monitoring and prevention.
        </li>
        <li className={styles['body_text']}>
          <strong>
            To evaluate and improve our Services, products, marketing, and your
            experience.
          </strong>{' '}
          We may process your information when we believe it is necessary to
          identify usage trends, determine the effectiveness of our promotional
          campaigns, and to evaluate and improve our Services, products,
          marketing, and your experience.
        </li>
        <li className={styles['body_text']}>
          <strong>To identify usage trends.</strong> We may process information
          about how you use our Services to better understand how they are being
          used so we can improve them.
        </li>
        <li className={styles['body_text']}>
          <strong>To comply with our legal obligations.</strong> We may process
          your information to comply with our legal obligations, respond to
          legal requests, and exercise, establish, or defend our legal rights.
        </li>
      </ul>
    </div>
    <div id="legalbases">
      <strong>
        <span className={styles['heading_1']}>
          <h2>
            3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
          </h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>In Short: </strong>We only process your personal information
        when we believe it is necessary and we have a valid legal reason (i.e.,
        legal basis) to do so under applicable law, like with your consent, to
        comply with laws, to provide you with services to enter into or fulfill
        our contractual obligations, to protect your rights, or to fulfill our
        legitimate business interests.
      </span>
      <div className={styles['body_text']}>
        <strong>
          If you are located in Canada, this section applies to you.
        </strong>
      </div>
      <div className={styles['body_text']}>
        We may process your information if you have given us specific permission
        (i.e., express consent) to use your personal information for a specific
        purpose, or in situations where your permission can be inferred (i.e.,
        implied consent). You can withdraw your consent at any time.
      </div>
      <div className={styles['body_text']}>
        In some exceptional cases, we may be legally permitted under applicable
        law to process your information without your consent, including, for
        example:
      </div>
      <ul>
        <li className={styles['body_text']}>
          If collection is clearly in the interests of an individual and consent
          cannot be obtained in a timely way
        </li>
        <li className={styles['body_text']}>
          For investigations and fraud detection and prevention
        </li>
        <li className={styles['body_text']}>
          For business transactions provided certain conditions are met
        </li>
        <li className={styles['body_text']}>
          If it is contained in a witness statement and the collection is
          necessary to assess, process, or settle an insurance claim
        </li>
        <li className={styles['body_text']}>
          For identifying injured, ill, or deceased persons and communicating
          with next of kin
        </li>
        <li className={styles['body_text']}>
          If we have reasonable grounds to believe an individual has been, is,
          or may be victim of financial abuse
        </li>
        <li className={styles['body_text']}>
          If it is reasonable to expect collection and use with consent would
          compromise the availability or the accuracy of the information and the
          collection is reasonable for purposes related to investigating a
          breach of an agreement or a contravention of the laws of Canada or a
          province
        </li>
        <li className={styles['body_text']}>
          If disclosure is required to comply with a subpoena, warrant, court
          order, or rules of the court relating to the production of records
        </li>
        <li className={styles['body_text']}>
          If it was produced by an individual in the course of their employment,
          business, or profession and the collection is consistent with the
          purposes for which the information was produced
        </li>
        <li className={styles['body_text']}>
          If the collection is solely for journalistic, artistic, or literary
          purposes
        </li>
        <li className={styles['body_text']}>
          If the information is publicly available and is specified by the
          regulations
        </li>
      </ul>
    </div>
    <div id="whoshare">
      <strong>
        <span className={styles['heading_1']}>
          <h2>4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short:</em>
        </strong>{' '}
        We may share information in specific situations described in this
        section and/or with the following third parties.
      </span>
      <div className={styles['body_text']}>
        We may need to share your personal information in the following
        situations:
      </div>
      <ul>
        <li className={styles['body_text']}>
          <strong>Business Transfers.</strong> We may share or transfer your
          information in connection with, or during negotiations of, any merger,
          sale of company assets, financing, or acquisition of all or a portion
          of our business to another company.
        </li>
      </ul>
    </div>
    <div id="cookies">
      <strong>
        <span className={styles['heading_1']}>
          <h2>5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short:</em>
        </strong>{' '}
        We may use cookies and other tracking technologies to collect and store
        your information.
      </span>
      <div className={styles['body_text']}>
        We may use cookies and similar tracking technologies (like web beacons
        and pixels) to gather information when you interact with our Services.
        Some online tracking technologies help us maintain the security of our
        Services and your account, prevent crashes, fix bugs, save your
        preferences, and assist with basic site functions.
      </div>
      <div className={styles['body_text']}>
        We also permit third parties and service providers to use online
        tracking technologies on our Services for analytics and advertising,
        including to help manage and display advertisements, to tailor
        advertisements to your interests, or to send abandoned shopping cart
        reminders (depending on your communication preferences). The third
        parties and service providers use their technology to provide
        advertising about products and services tailored to your interests which
        may appear either on our Services or on other websites.
      </div>
      <div className={styles['body_text']}>
        To the extent these online tracking technologies are deemed to be a
        "sale"/"sharing" (which includes targeted advertising, as defined under
        the applicable laws) under applicable US state laws, you can opt out of
        these online tracking technologies by submitting a request as described
        below under section{' '}
        <a
          className={styles['link']}
          href="#uslaws"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
        </a>
        .
      </div>
      <div className={styles['body_text']}>
        Specific information about how we use such technologies and how you can
        refuse certain cookies is set out in our Cookie Notice.
      </div>
      <div className={styles['body_text']}>
        <strong>
          <span className={styles['heading_2']}>
            <h3>Google Analytics</h3>
          </span>
        </strong>{' '}
        We may share your information with Google Analytics to track and analyze
        the use of the Services. To opt out of being tracked by Google Analytics
        across the Services, visit{' '}
        <a
          className={styles['link']}
          href="https://tools.google.com/dlpage/gaoptout"
          rel="noopener noreferrer"
          target="_blank"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          https://tools.google.com/dlpage/gaoptout
        </a>
        . For more information on the privacy practices of Google, please visit
        the{' '}
        <a
          className={styles['link']}
          href="https://policies.google.com/privacy"
          rel="noopener noreferrer"
          target="_blank"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          Google Privacy & Terms page
        </a>
        .
      </div>
    </div>
    <div id="inforetain">
      <strong>
        <span className={styles['heading_1']}>
          <h2>6. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short: </em>
        </strong>
        We keep your information for as long as necessary to fulfill the
        purposes outlined in this Privacy Notice unless otherwise required by
        law.
      </span>
      <div className={styles['body_text']}>
        We will only keep your personal information for as long as it is
        necessary for the purposes set out in this Privacy Notice, unless a
        longer retention period is required or permitted by law (such as tax,
        accounting, or other legal requirements). No purpose in this notice will
        require us keeping your personal information for longer than the period
        of time in which users have an account with us.
      </div>
      <div className={styles['body_text']}>
        When we have no ongoing legitimate business need to process your
        personal information, we will either delete or anonymize such
        information, or, if this is not possible (for example, because your
        personal information has been stored in backup archives), then we will
        securely store your personal information and isolate it from any further
        processing until deletion is possible.
      </div>
    </div>
    <div id="infosafe">
      <strong>
        <span className={styles['heading_1']}>
          <h2>7. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short: </em>
        </strong>
        We aim to protect your personal information through a system of
        organizational and technical security measures.
      </span>
      <div className={styles['body_text']}>
        We have implemented appropriate and reasonable technical and
        organizational security measures designed to protect the security of any
        personal information we process. However, despite our safeguards and
        efforts to secure your information, no electronic transmission over the
        Internet or information storage technology can be guaranteed to be 100%
        secure, so we cannot promise or guarantee that hackers, cybercriminals,
        or other unauthorized third parties will not be able to defeat our
        security and improperly collect, access, steal, or modify your
        information. Although we will do our best to protect your personal
        information, transmission of personal information to and from our
        Services is at your own risk. You should only access the Services within
        a secure environment.
      </div>
    </div>
    <div id="infominors">
      <strong>
        <span className={styles['heading_1']}>
          <h2>8. DO WE COLLECT INFORMATION FROM MINORS?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short:</em>
        </strong>{' '}
        We do not knowingly collect data from or market to children under 18
        years of age.
      </span>
      <div className={styles['body_text']}>
        We do not knowingly collect, solicit data from, or market to children
        under 18 years of age, nor do we knowingly sell such personal
        information. By using the Services, you represent that you are at least
        18 or that you are the parent or guardian of such a minor and consent to
        such minor dependent's use of the Services. If we learn that personal
        information from users less than 18 years of age has been collected, we
        will deactivate the account and take reasonable measures to promptly
        delete such data from our records. If you become aware of any data we
        may have collected from children under age 18, please contact us at{' '}
        <a
          href="mailto:notices@aquifer.cloud"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          notices@aquifer.cloud
        </a>
        .
      </div>
    </div>
    <div id="privacyrights">
      <strong>
        <span className={styles['heading_1']}>
          <h2>9. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short:</em>
        </strong>{' '}
        Depending on your state of residence in the US or in some regions, such
        as Canada, you have rights that allow you greater access to and control
        over your personal information. You may review, change, or terminate
        your account at any time, depending on your country, province, or state
        of residence.
      </span>
      <div className={styles['body_text']}>
        In some regions (like Canada), you have certain rights under applicable
        data protection laws. These may include the right (i) to request access
        and obtain a copy of your personal information, (ii) to request
        rectification or erasure; (iii) to restrict the processing of your
        personal information; (iv) if applicable, to data portability; and (v)
        not to be subject to automated decision-making. In certain
        circumstances, you may also have the right to object to the processing
        of your personal information. You can make such a request by contacting
        us by using the contact details provided in the section{' '}
        <a
          className={styles['link']}
          href="#contact"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
        </a>{' '}
        below.
      </div>
      <div className={styles['body_text']}>
        We will consider and act upon any request in accordance with applicable
        data protection laws.
      </div>
      <div className={styles['body_text']}>
        <strong>
          <u>Withdrawing your consent:</u>
        </strong>{' '}
        If we are relying on your consent to process your personal information,
        which may be express and/or implied consent depending on the applicable
        law, you have the right to withdraw your consent at any time. You can
        withdraw your consent at any time by contacting us by using the contact
        details provided in the section{' '}
        <a
          className={styles['link']}
          href="#contact"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
        </a>{' '}
        below.
      </div>
      <div className={styles['body_text']}>
        However, please note that this will not affect the lawfulness of the
        processing before its withdrawal nor, when applicable law allows, will
        it affect the processing of your personal information conducted in
        reliance on lawful processing grounds other than consent.
      </div>
      <span className={styles['heading_2']}>
        <strong>
          <h3>Account Information</h3>
        </strong>
      </span>
      <div className={styles['body_text']}>
        If you would at any time like to review or change the information in
        your account or terminate your account, you can:
      </div>
      <ul>
        <li className={styles['body_text']}>
          Contact us using the contact information provided.
        </li>
      </ul>
      <div className={styles['body_text']}>
        Upon your request to terminate your account, we will deactivate or
        delete your account and information from our active databases. However,
        we may retain some information in our files to prevent fraud,
        troubleshoot problems, assist with any investigations, enforce our legal
        terms and/or comply with applicable legal requirements.
      </div>
      <div className={styles['body_text']}>
        <strong>
          <u>Cookies and similar technologies:</u>
        </strong>{' '}
        Most Web browsers are set to accept cookies by default. If you prefer,
        you can usually choose to set your browser to remove cookies and to
        reject cookies. If you choose to remove cookies or reject cookies, this
        could affect certain features or services of our Services.
      </div>
      <div className={styles['body_text']}>
        If you have questions or comments about your privacy rights, you may
        email us at{' '}
        <a
          href="mailto:notices@aquifer.cloud"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          notices@aquifer.cloud
        </a>
        .
      </div>
    </div>
    <div id="DNT">
      <strong>
        <span className={styles['heading_1']}>
          <h2>10. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
        </span>
      </strong>
      <div className={styles['body_text']}>
        Most web browsers and some mobile operating systems and mobile
        applications include a Do-Not-Track ("DNT") feature or setting you can
        activate to signal your privacy preference not to have data about your
        online browsing activities monitored and collected. At this stage, no
        uniform technology standard for recognizing and implementing DNT signals
        has been finalized. As such, we do not currently respond to DNT browser
        signals or any other mechanism that automatically communicates your
        choice not to be tracked online. If a standard for online tracking is
        adopted that we must follow in the future, we will inform you about that
        practice in a revised version of this Privacy Notice.
      </div>
      <div className={styles['body_text']}>
        California law requires us to let you know how we respond to web browser
        DNT signals. Because there currently is not an industry or legal
        standard for recognizing or honoring DNT signals, we do not respond to
        them at this time.
      </div>
    </div>
    <div id="uslaws">
      <strong>
        <span className={styles['heading_1']}>
          <h2>11. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <strong>
          <em>In Short: </em>
        </strong>
        If you are a resident of California, Colorado, Connecticut, Delaware,
        Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana,
        Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee,
        Texas, Utah, or Virginia, you may have the right to request access to
        and receive details about the personal information we maintain about you
        and how we have processed it, correct inaccuracies, get a copy of, or
        delete your personal information. You may also have the right to
        withdraw your consent to our processing of your personal information.
        These rights may be limited in some circumstances by applicable law.
        More information is provided below.
      </span>
      <span className={styles['heading_2']}>
        <strong>
          <h3>Categories of Personal Information We Collect</h3>
        </strong>
      </span>
      <div className={styles['body_text']}>
        The table below shows the categories of personal information we have
        collected in the past twelve (12) months. The table includes
        illustrative examples of each category and does not reflect the personal
        information we collect from you. For a comprehensive inventory of all
        personal information we process, please refer to the section{' '}
        <a
          className={styles['link']}
          href="#infocollect"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          WHAT INFORMATION DO WE COLLECT?
        </a>
        .
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th
              style={{
                width: '33.8%',
                border: '1px solid black',
                textAlign: 'left',
              }}
            >
              Category
            </th>
            <th
              style={{
                width: '51.4%',
                border: '1px solid black',
                textAlign: 'left',
              }}
            >
              Examples
            </th>
            <th
              style={{
                width: '14.9%',
                border: '1px solid black',
                textAlign: 'left',
              }}
            >
              Collected
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ border: '1px solid black' }}>A. Identifiers</td>
            <td style={{ border: '1px solid black' }}>
              Contact details, such as real name, alias, postal address,
              telephone or mobile contact number, unique personal identifier,
              online identifier, Internet Protocol address, email address, and
              account name
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              B. Personal information as defined in the California Customer
              Records statute
            </td>
            <td style={{ border: '1px solid black' }}>
              Name, contact information, education, employment, employment
              history, and financial information
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              C. Protected classification characteristics under state or federal
              law
            </td>
            <td style={{ border: '1px solid black' }}>
              Gender, age, date of birth, race and ethnicity, national origin,
              marital status, and other demographic data
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              D. Commercial information
            </td>
            <td style={{ border: '1px solid black' }}>
              Transaction information, purchase history, financial details, and
              payment information
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              E. Biometric information
            </td>
            <td style={{ border: '1px solid black' }}>
              Fingerprints and voiceprints
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              F. Internet or other similar network activity
            </td>
            <td style={{ border: '1px solid black' }}>
              Browsing history, search history, online behavior, interest data,
              and interactions with our and other websites, applications,
              systems, and advertisements
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>G. Geolocation data</td>
            <td style={{ border: '1px solid black' }}>Device location</td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              H. Audio, electronic, sensory, or similar information
            </td>
            <td style={{ border: '1px solid black' }}>
              Images and audio, video or call recordings created in connection
              with our business activities
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              I. Professional or employment-related information
            </td>
            <td style={{ border: '1px solid black' }}>
              Business contact details in order to provide you our Services at a
              business level or job title, work history, and professional
              qualifications if you apply for a job with us
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              J. Education Information
            </td>
            <td style={{ border: '1px solid black' }}>
              Student records and directory information
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              K. Inferences drawn from collected personal information
            </td>
            <td style={{ border: '1px solid black' }}>
              Inferences drawn from any of the collected personal information
              listed above to create a profile or summary about, for example, an
              individual's preferences and characteristics
            </td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid black' }}>
              L. Sensitive personal Information
            </td>
            <td style={{ border: '1px solid black' }}></td>
            <td style={{ border: '1px solid black' }}>NO</td>
          </tr>
        </tbody>
      </table>
      <div className={styles['body_text']}>
        We may also collect other personal information outside of these
        categories through instances where you interact with us in person,
        online, or by phone or mail in the context of:
      </div>
      <ul>
        <li className={styles['body_text']}>
          Receiving help through our customer support channels;
        </li>
        <li className={styles['body_text']}>
          Participation in customer surveys or contests; and
        </li>
        <li className={styles['body_text']}>
          Facilitation in the delivery of our Services and to respond to your
          inquiries.
        </li>
      </ul>
      <span className={styles['heading_2']}>
        <strong>
          <h3>Your Rights</h3>
        </strong>
      </span>
      <div className={styles['body_text']}>
        You have rights under certain US state data protection laws. However,
        these rights are not absolute, and in certain cases, we may decline your
        request as permitted by law. These rights include:
      </div>
      <ul>
        <li className={styles['body_text']}>
          Right to know whether or not we are processing your personal data
        </li>
        <li className={styles['body_text']}>
          Right to access your personal data
        </li>
        <li className={styles['body_text']}>
          Right to correct inaccuracies in your personal data
        </li>
        <li className={styles['body_text']}>
          Right to request the deletion of your personal data
        </li>
        <li className={styles['body_text']}>
          Right to obtain a copy of the personal data you previously shared with
          us
        </li>
        <li className={styles['body_text']}>
          Right to non-discrimination for exercising your rights
        </li>
        <li className={styles['body_text']}>
          Right to opt out of the processing of your personal data if it is used
          for targeted advertising (or sharing as defined under California's
          privacy law), the sale of personal data, or profiling in furtherance
          of decisions that produce legal or similarly significant effects
          ("profiling")
        </li>
      </ul>
      <div className={styles['body_text']}>
        Depending upon the state where you live, you may also have the following
        rights:
      </div>
      <ul>
        <li className={styles['body_text']}>
          Right to access the categories of personal data being processed (as
          permitted by applicable law, including the privacy law in Minnesota)
        </li>
        <li className={styles['body_text']}>
          Right to obtain a list of the categories of third parties to which we
          have disclosed personal data (as permitted by applicable law,
          including the privacy law in California, Delaware, and Maryland)
        </li>
        <li className={styles['body_text']}>
          Right to obtain a list of specific third parties to which we have
          disclosed personal data (as permitted by applicable law, including the
          privacy law in Minnesota and Oregon)
        </li>
        <li className={styles['body_text']}>
          Right to review, understand, question, and correct how personal data
          has been profiled (as permitted by applicable law, including the
          privacy law in Minnesota)
        </li>
        <li className={styles['body_text']}>
          Right to limit use and disclosure of sensitive personal data (as
          permitted by applicable law, including the privacy law in California)
        </li>
        <li className={styles['body_text']}>
          Right to opt out of the collection of sensitive data and personal data
          collected through the operation of a voice or facial recognition
          feature (as permitted by applicable law, including the privacy law in
          Florida)
        </li>
      </ul>
      <span className={styles['heading_2']}>
        <strong>
          <h3>How to Exercise Your Rights</h3>
        </strong>
      </span>
      <div className={styles['body_text']}>
        To exercise these rights, you can contact us by submitting a{' '}
        <a
          className={styles['link']}
          href="https://app.termly.io/notify/cd8bac8a-12fd-469c-99ac-de2ab0a6d7a4"
          rel="noopener noreferrer"
          target="_blank"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          data subject access request
        </a>
        , by emailing us at{' '}
        <a
          href="mailto:notices@aquifer.cloud"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          notices@aquifer.cloud
        </a>
        , or by referring to the contact details at the bottom of this document.
      </div>
      <div className={styles['body_text']}>
        Under certain US state data protection laws, you can designate an
        authorized agent to make a request on your behalf. We may deny a request
        from an authorized agent that does not submit proof that they have been
        validly authorized to act on your behalf in accordance with applicable
        laws.
      </div>
      <span className={styles['heading_2']}>
        <strong>
          <h3>Request Verification</h3>
        </strong>
      </span>
      <div className={styles['body_text']}>
        Upon receiving your request, we will need to verify your identity to
        determine you are the same person about whom we have the information in
        our system. We will only use personal information provided in your
        request to verify your identity or authority to make the request.
        However, if we cannot verify your identity from the information already
        maintained by us, we may request that you provide additional information
        for the purposes of verifying your identity and for security or
        fraud-prevention purposes.
      </div>
      <div className={styles['body_text']}>
        If you submit the request through an authorized agent, we may need to
        collect additional information to verify your identity before processing
        your request and the agent will need to provide a written and signed
        permission from you to submit such request on your behalf.
      </div>
      <span className={styles['heading_2']}>
        <strong>
          <h3>Appeals</h3>
        </strong>
      </span>
      <div className={styles['body_text']}>
        Under certain US state data protection laws, if we decline to take
        action regarding your request, you may appeal our decision by emailing
        us at{' '}
        <a
          href="mailto:notices@aquifer.cloud"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          notices@aquifer.cloud
        </a>
        . We will inform you in writing of any action taken or not taken in
        response to the appeal, including a written explanation of the reasons
        for the decisions. If your appeal is denied, you may submit a complaint
        to your state attorney general.
      </div>
      <span className={styles['heading_2']}>
        <strong>
          <h3>California "Shine The Light" Law</h3>
        </strong>
      </span>
      <div className={styles['body_text']}>
        California Civil Code Section 1798.83, also known as the "Shine The
        Light" law, permits our users who are California residents to request
        and obtain from us, once a year and free of charge, information about
        categories of personal information (if any) we disclosed to third
        parties for direct marketing purposes and the names and addresses of all
        third parties with which we shared personal information in the
        immediately preceding calendar year. If you are a California resident
        and would like to make such a request, please submit your request in
        writing to us by using the contact details provided in the section{' '}
        <a
          className={styles['link']}
          href="#contact"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
        </a>{' '}
        below.
      </div>
    </div>
    <div
      id="policyupdates"
      style={{ lineHeight: 1.5, fontSize: 15, color: 'rgb(0,0,0)' }}
    >
      <strong>
        <span className={styles['heading_1']}>
          <h2>12. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
        </span>
      </strong>
      <span className={styles['body_text']}>
        <em>
          <strong>In Short: </strong>Yes, we will update this notice as
          necessary to stay compliant with relevant laws.
        </em>
      </span>
      <div className={styles['body_text']}>
        We may update this Privacy Notice from time to time. The updated version
        will be indicated by an updated "Revised" date at the top of this
        Privacy Notice. If we make material changes to this Privacy Notice, we
        may notify you either by prominently posting a notice of such changes or
        by directly sending you a notification. We encourage you to review this
        Privacy Notice frequently to be informed of how we are protecting your
        information.
      </div>
    </div>
    <div
      id="contact"
      style={{ lineHeight: 1.5, fontSize: 15, color: 'rgb(0,0,0)' }}
    >
      <strong>
        <span className={styles['heading_1']}>
          <h2>13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
        </span>
      </strong>
      <div className={styles['body_text']}>
        If you have questions or comments about this notice, you may email us at{' '}
        <a
          href="mailto:notices@aquifer.cloud"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          notices@aquifer.cloud
        </a>{' '}
        or contact us by post at:
      </div>
      <div className={styles['body_text']}>
        <strong>Aquifer, Inc</strong>
      </div>
      <div className={styles['body_text']}>__________</div>
      <div className={styles['body_text']}>__________</div>
    </div>
    <div
      id="request"
      style={{ lineHeight: 1.5, fontSize: 15, color: 'rgb(0,0,0)' }}
    >
      <strong>
        <span className={styles['heading_1']}>
          <h2>
            14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </h2>
        </span>
      </strong>
      <div className={styles['body_text']}>
        Based on the applicable laws of your country or state of residence in
        the US, you may have the right to request access to the personal
        information we collect from you, details about how we have processed it,
        correct inaccuracies, or delete your personal information. You may also
        have the right to withdraw your consent to our processing of your
        personal information. These rights may be limited in some circumstances
        by applicable law. To request to review, update, or delete your personal
        information, please{' '}
        <a
          className={styles['link']}
          href="https://app.termly.io/notify/cd8bac8a-12fd-469c-99ac-de2ab0a6d7a4"
          rel="noopener noreferrer"
          target="_blank"
          style={{ color: 'rgb(0, 58, 250)' }}
        >
          fill out and submit a data subject access request
        </a>
        .
      </div>
    </div>
  </div>
);
